import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSupabaseUser } from "../contexts/SupabaseUserContext";
import { useGlobalAuth } from "../contexts/GlobalContext";
import {
  updateUserProfile,
  updateUserPassword,
  deleteAccount,
  signOut,
} from "../api/SupabaseApi";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import Icon from "../components/AppIcon";

const ProfileSettings = () => {
  const navigate = useNavigate();
  const supabaseUser = useSupabaseUser();
  const { spotify, youtube } = useGlobalAuth();

  const [activeTab, setActiveTab] = useState("profile");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  // Profile form state
  const [profileForm, setProfileForm] = useState({
    full_name: "",
    email: "",
  });

  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Password visibility states
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  // Account connections state
  const [connections, setConnections] = useState({
    spotify: {
      connected: false,
      username: "",
      email: "",
      lastSync: null,
    },
    youtube: {
      connected: false,
      username: "",
      email: "",
      lastSync: null,
    },
  });

  // Delete account state
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    if (supabaseUser) {
      setProfileForm({
        full_name: supabaseUser.user_metadata?.full_name || "",
        email: supabaseUser.email || "",
      });
    }
  }, [supabaseUser]);

  useEffect(() => {
    // Update connections based on auth state
    setConnections({
      spotify: {
        connected: spotify.isLoggedIn,
        username: spotify.spotifyUserInfo?.display_name || "",
        email: spotify.spotifyUserInfo?.email || "",
        lastSync: null,
      },
      youtube: {
        connected: !!youtube.googleToken,
        username: youtube.googleUserInfo?.name || "",
        email: youtube.googleUserInfo?.email || "",
        lastSync: null,
      },
    });
  }, [
    spotify.isLoggedIn,
    spotify.spotifyUserInfo,
    youtube.googleToken,
    youtube.googleUserInfo,
  ]);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), 5000);
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await updateUserProfile({
        full_name: profileForm.full_name,
      });

      if (error) {
        showMessage("error", error.message);
      } else {
        showMessage("success", "Profile updated successfully!");
      }
    } catch (error) {
      showMessage("error", "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showMessage("error", "New passwords do not match");
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      showMessage("error", "Password must be at least 6 characters long");
      return;
    }

    setLoading(true);

    try {
      const { error } = await updateUserPassword(passwordForm.newPassword);

      if (error) {
        showMessage("error", error.message);
      } else {
        showMessage("success", "Password updated successfully!");
        setPasswordForm({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      }
    } catch (error) {
      showMessage("error", "Failed to update password");
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnectAccount = (platform) => {
    if (platform === "spotify") {
      spotify.spotifyLogout();
      showMessage("success", "Spotify account disconnected");
    } else if (platform === "youtube") {
      youtube.youtubeLogout();
      showMessage("success", "YouTube account disconnected");
    }
  };

  const handleConnectAccount = (platform) => {
    if (platform === "spotify") {
      spotify.spotifyLogin();
    } else if (platform === "youtube") {
      youtube.youtubeLogin();
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== "DELETE") {
      showMessage("error", "Please type DELETE to confirm");
      return;
    }
    setLoading(true);
    try {
      const { error } = await deleteAccount();
      if (error) {
        showMessage("error", "Failed to delete account: " + error.message);
      } else {
        showMessage("success", "Account signed out successfully. For complete account deletion, please contact support.");
        setShowDeleteModal(false);
        // Navigate to home after a short delay
        setTimeout(() => {
          navigate("/");
        }, 2000);
      }
    } catch (error) {
      showMessage("error", "Failed to delete account: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: "profile", label: "Profile", icon: "User" },
    { id: "security", label: "Security", icon: "Lock" },
    { id: "connections", label: "Connections", icon: "Link" },
    { id: "danger", label: "Danger Zone", icon: "AlertTriangle" },
  ];

  const renderProfileTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-text-primary mb-4">
          Personal Information
        </h3>
        <form onSubmit={handleProfileUpdate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Full Name
            </label>
            <Input
              type="text"
              value={profileForm.full_name}
              onChange={(e) =>
                setProfileForm({ ...profileForm, full_name: e.target.value })
              }
              placeholder="Enter your full name"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Email Address
            </label>
            <Input
              type="email"
              value={profileForm.email}
              disabled
              className="bg-surface-secondary"
            />
            <p className="text-xs text-text-secondary mt-1">
              Email address cannot be changed from this page
            </p>
          </div>

          <Button
            type="submit"
            variant="primary"
            loading={loading}
            className="w-full sm:w-auto"
          >
            Update Profile
          </Button>
        </form>
      </div>
    </div>
  );

  const renderSecurityTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-text-primary mb-4">
          Change Password
        </h3>
        <form onSubmit={handlePasswordChange} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Current Password
            </label>
            <div className="relative">
              <Input
                type={showPasswords.current ? "text" : "password"}
                value={passwordForm.currentPassword}
                onChange={(e) =>
                  setPasswordForm({
                    ...passwordForm,
                    currentPassword: e.target.value,
                  })
                }
                placeholder="Enter current password"
                required
                className="pr-10"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-text-secondary hover:text-text-primary transition-colors"
                onClick={() =>
                  setShowPasswords({
                    ...showPasswords,
                    current: !showPasswords.current,
                  })
                }
              >
                <Icon
                  name={showPasswords.current ? "EyeOff" : "Eye"}
                  size={18}
                />
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              New Password
            </label>
            <div className="relative">
              <Input
                type={showPasswords.new ? "text" : "password"}
                value={passwordForm.newPassword}
                onChange={(e) =>
                  setPasswordForm({
                    ...passwordForm,
                    newPassword: e.target.value,
                  })
                }
                placeholder="Enter new password"
                required
                className="pr-10"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-text-secondary hover:text-text-primary transition-colors"
                onClick={() =>
                  setShowPasswords({
                    ...showPasswords,
                    new: !showPasswords.new,
                  })
                }
              >
                <Icon name={showPasswords.new ? "EyeOff" : "Eye"} size={18} />
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Confirm New Password
            </label>
            <div className="relative">
              <Input
                type={showPasswords.confirm ? "text" : "password"}
                value={passwordForm.confirmPassword}
                onChange={(e) =>
                  setPasswordForm({
                    ...passwordForm,
                    confirmPassword: e.target.value,
                  })
                }
                placeholder="Confirm new password"
                required
                className="pr-10"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-text-secondary hover:text-text-primary transition-colors"
                onClick={() =>
                  setShowPasswords({
                    ...showPasswords,
                    confirm: !showPasswords.confirm,
                  })
                }
              >
                <Icon
                  name={showPasswords.confirm ? "EyeOff" : "Eye"}
                  size={18}
                />
              </button>
            </div>
          </div>

          <Button
            type="submit"
            variant="primary"
            loading={loading}
            className="w-full sm:w-auto"
          >
            Change Password
          </Button>
        </form>
      </div>
    </div>
  );

  const renderConnectionsTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-text-primary mb-4">
          Connected Accounts
        </h3>
        <div className="space-y-4">
          {/* Spotify Connection */}
          <div className="border border-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                  <Icon name="Music" size={20} className="text-white" />
                </div>
                <div>
                  <h4 className="font-medium text-text-primary">Spotify</h4>
                  <p className="text-sm text-text-secondary">
                    {connections.spotify.connected
                      ? `Connected as ${
                          connections.spotify.username || "Unknown"
                        }`
                      : "Not connected"}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <div
                  className={`w-2 h-2 rounded-full ${
                    connections.spotify.connected ? "bg-success" : "bg-error"
                  }`}
                />
                {connections.spotify.connected ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDisconnectAccount("spotify")}
                  >
                    Disconnect
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleConnectAccount("spotify")}
                  >
                    Connect
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* YouTube Connection */}
          <div className="border border-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
                  <Icon name="Play" size={20} className="text-white" />
                </div>
                <div>
                  <h4 className="font-medium text-text-primary">
                    YouTube Music
                  </h4>
                  <p className="text-sm text-text-secondary">
                    {connections.youtube.connected
                      ? `Connected as ${
                          connections.youtube.username || "Unknown"
                        }`
                      : "Not connected"}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <div
                  className={`w-2 h-2 rounded-full ${
                    connections.youtube.connected ? "bg-success" : "bg-error"
                  }`}
                />
                {connections.youtube.connected ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDisconnectAccount("youtube")}
                  >
                    Disconnect
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleConnectAccount("youtube")}
                  >
                    Connect
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDangerTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-text-primary mb-4">
          Danger Zone
        </h3>

        {/* Sign Out */}
        <div className="border border-border rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-text-primary">Sign Out</h4>
              <p className="text-sm text-text-secondary">
                Sign out of your account on this device
              </p>
            </div>
            <Button variant="outline" onClick={signOut}>
              Sign Out
            </Button>
          </div>
        </div>

        {/* Delete Account */}
        <div className="border border-error rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-error">Sign Out & Clear Data</h4>
              <p className="text-sm text-text-secondary">
                Sign out and clear local data. Contact support for complete account deletion.
              </p>
            </div>
            <Button variant="danger" onClick={() => setShowDeleteModal(true)}>
              Sign Out & Clear
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background pt-32 lg:pt-36">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-text-primary mb-2">
            Profile Settings
          </h1>
          <p className="text-text-secondary">
            Manage your account settings and connected services
          </p>
        </div>

        {/* Message Display */}
        {message.text && (
          <div
            className={`mb-6 p-4 rounded-lg ${
              message.type === "success"
                ? "bg-success/10 border border-success/20 text-success"
                : "bg-error/10 border border-error/20 text-error"
            }`}
          >
            {message.text}
          </div>
        )}

        {/* Tab Navigation */}
        <div className="border-b border-border mb-8">
          <nav className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? "border-primary text-primary"
                    : "border-transparent text-text-secondary hover:text-text-primary"
                }`}
              >
                <Icon name={tab.icon} size={16} />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="bg-surface rounded-lg border border-border p-6">
          {activeTab === "profile" && renderProfileTab()}
          {activeTab === "security" && renderSecurityTab()}
          {activeTab === "connections" && renderConnectionsTab()}
          {activeTab === "danger" && renderDangerTab()}
        </div>
      </div>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-error mb-4">
              Sign Out & Clear Data
            </h3>
            <p className="text-text-secondary mb-4">
              This will sign you out and clear your local data. For complete account deletion including all stored data, please contact our support team.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-text-primary mb-2">
                Type DELETE to confirm
              </label>
              <Input
                type="text"
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder="DELETE"
              />
            </div>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowDeleteModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteAccount}
                loading={loading}
                className="flex-1"
              >
                Sign Out & Clear
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileSettings;
